import { observer } from 'mobx-react-lite';
import React from 'react';
import {
    Icon,
    Button,
    Menu,
    MenuContent,
    MenuRadioGroup,
    MenuRadioItem,
    MenuSeparator,
    MenuTrigger,
} from '@cad-arc';
import { ColumnDefinitionEnumType } from '@mark43/rms-api';
import { GroupingDirective } from '@list-store';
import { useQueueStore } from '../../queues/context/use-queue-store';
import { useColumnDisplayNames } from '../../queues/hooks/use-column-display-names';
import { WorkspaceQueueDto } from '../../../../../../api-ts-packages/cad-api';
import { ConcreteColumnDefinitionData } from '../../queues/column-definition.enum';
import { useSetFrBoloQueueFilterState } from '../../fr-events-list-page/state/fr-shared-queue-filters-context';
import { WorkspaceQueue } from '../../panels/workspace-queue';
import { UnitQueueRowData } from '../../socket/stream-types';
import { useFrUnitIdsToShowAndUnitsGroupingData } from '../../unit-queues/use-fr-unit-ids-to-show-and-units-grouping-data';
import {
    REGULAR_ASCENDING_SORT_DIRECTION,
    REGULAR_DESCENDING_SORT_DIRECTION,
    UNIT_BEAT_COLUMN_DEFINITION,
    UNIT_DISPATCH_AREA_COLUMN_DEFINITION,
} from '../../unit-queues/constants';
import { getGroupComparator } from '../../unit-queues/utils';

export const FrSortMenu = observer(function FrSortMenu({
    compact,
    columns,
    defaultColumnDefinition,
    defaultSortDirection = REGULAR_ASCENDING_SORT_DIRECTION,
}: {
    columns: ConcreteColumnDefinitionData[];
    defaultColumnDefinition: WorkspaceQueueDto['sortByQueueColumnDefinition'];
    defaultSortDirection?: WorkspaceQueueDto['sortDirection'];
    compact?: boolean;
}): React.ReactElement {
    const queueStore = useQueueStore();
    const columnDisplayNames = useColumnDisplayNames();
    const isFrUnitQueue = queueStore.workspacePanel.queue.dataEntityType === 'UNIT';

    const columnDefinition =
        (isFrUnitQueue
            ? queueStore.workspacePanel.queue.groupByQueueColumnDefinition
            : queueStore.workspacePanel.queue.sortByQueueColumnDefinition) ??
        defaultColumnDefinition;

    const sortDirection =
        (isFrUnitQueue
            ? queueStore.workspacePanel.queue.groupBySortDirection
            : queueStore.workspacePanel.queue.sortDirection) ?? defaultSortDirection;

    const { setQueueListSortingDirective } = useSetFrBoloQueueFilterState();
    const { getGroupValueData } = useFrUnitIdsToShowAndUnitsGroupingData();

    const getBoloColumnDefinition = (
        columnDefinition?: string
    ): WorkspaceQueueDto['sortByQueueColumnDefinition'] => {
        return columnDefinition === 'BEAT'
            ? 'BOLO_SUBDIVISIONS'
            : (columnDefinition as WorkspaceQueueDto['sortByQueueColumnDefinition']);
    };

    const setGroupingDirective = (
        columnDefinition: ColumnDefinitionEnumType,
        sortDirection: string
    ) => {
        const getGroupValue = getGroupValueData(columnDefinition);
        const comparator = getGroupComparator(sortDirection, columnDefinition);

        queueStore.listStore.setGroupingDirective({
            getGroupValue,
            comparator,
        } as GroupingDirective<UnitQueueRowData>);
    };

    const onValueChange = (newColumnDefinition: string) => {
        if (newColumnDefinition === columnDefinition) {
            return;
        }

        if (
            newColumnDefinition === UNIT_DISPATCH_AREA_COLUMN_DEFINITION ||
            newColumnDefinition === UNIT_BEAT_COLUMN_DEFINITION
        ) {
            queueStore.setGroupByQueueColumnDefinition(
                newColumnDefinition as WorkspaceQueueDto['groupByQueueColumnDefinition']
            );

            setGroupingDirective(newColumnDefinition, REGULAR_ASCENDING_SORT_DIRECTION);
        } else {
            queueStore.setSort(
                newColumnDefinition as WorkspaceQueueDto['sortByQueueColumnDefinition'],
                sortDirection,
                false,
                { skipDefaultSort: true }
            );

            if (isFrUnitQueue) {
                queueStore.workspacePanel.queue.setGroupBySortDirection(
                    REGULAR_ASCENDING_SORT_DIRECTION
                );

                setGroupingDirective(
                    newColumnDefinition as ColumnDefinitionEnumType,
                    REGULAR_ASCENDING_SORT_DIRECTION
                );

                queueStore.workspacePanel.queue.setGroupByColumn(
                    newColumnDefinition as ColumnDefinitionEnumType
                );
            }
        }
        setQueueListSortingDirective(getBoloColumnDefinition(columnDefinition), sortDirection);
    };

    const onChangeSortDirection = (sortingDirection: string) => {
        if (
            columnDefinition === UNIT_DISPATCH_AREA_COLUMN_DEFINITION ||
            columnDefinition === UNIT_BEAT_COLUMN_DEFINITION
        ) {
            queueStore.setGroupBySortDirection(sortingDirection as WorkspaceQueue['sortDirection']);

            setGroupingDirective(columnDefinition, sortingDirection);
        } else {
            queueStore.setSort(
                columnDefinition,
                sortingDirection as WorkspaceQueue['sortDirection'],
                false,
                { skipDefaultSort: true }
            );

            if (isFrUnitQueue) {
                setGroupingDirective(
                    columnDefinition as ColumnDefinitionEnumType,
                    sortingDirection
                );

                queueStore.workspacePanel.queue.setGroupBySortDirection(
                    sortingDirection as WorkspaceQueue['groupBySortDirection']
                );
            }
        }

        setQueueListSortingDirective(
            getBoloColumnDefinition(columnDefinition),
            sortingDirection as WorkspaceQueue['sortDirection']
        );
    };

    return (
        <Menu>
            <MenuTrigger asChild>
                <Button data-testid="fr-sort-menu-button" trailingVisual="Open" variant="outline">
                    <>
                        {!compact && 'Sort '}
                        {!compact && columnDefinition && (
                            <span>{`: ${columnDisplayNames?.[columnDefinition]?.displayName}`}</span>
                        )}
                        <Icon
                            icon={
                                sortDirection === REGULAR_ASCENDING_SORT_DIRECTION
                                    ? 'ArrowDownNarrow'
                                    : 'ArrowUpNarrow'
                            }
                            size="sm"
                            color="selected.default"
                            style={{ marginLeft: '0px' }}
                        />
                    </>
                </Button>
            </MenuTrigger>
            <MenuContent style={{ paddingRight: '0', minWidth: '205px' }}>
                <MenuRadioGroup onValueChange={onValueChange} value={columnDefinition}>
                    <div
                        className="custom-scrollbar"
                        style={{
                            overflowY: 'auto',
                        }}
                    >
                        {columns.map((column) => (
                            <MenuRadioItem key={column.name} value={column.name}>
                                {columnDisplayNames[column.name].displayName}
                            </MenuRadioItem>
                        ))}
                    </div>
                </MenuRadioGroup>

                {columnDefinition && (
                    <>
                        <MenuSeparator />
                        <MenuRadioGroup onValueChange={onChangeSortDirection} value={sortDirection}>
                            <MenuRadioItem value={REGULAR_ASCENDING_SORT_DIRECTION}>
                                Sort Ascending
                            </MenuRadioItem>
                            <MenuRadioItem value={REGULAR_DESCENDING_SORT_DIRECTION}>
                                Sort Descending
                            </MenuRadioItem>
                        </MenuRadioGroup>
                    </>
                )}
            </MenuContent>
        </Menu>
    );
});
